import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import {
  fetchChatHistory,
  ChatHistoryMessage,
} from "../store/slices/chatHistorySlice";
import { ArrowLeft, MessageCircle } from "lucide-react";
import Loader from "../components/ui/Loader";
import { usePageTitle } from "../hooks/usePageTitle";

const ChatHistoryPage: React.FC = () => {
  const { chatId } = useParams<{ chatId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    chat,
    messages,
    isLoading,
    error,
    currentPage,
    hasNextPage,
    hasPrevPage,
  } = useAppSelector((state: any) => state.chatHistory);
  //   const [newMessage, setNewMessage] = useState("");

  usePageTitle("Chat History");

  useEffect(() => {
    if (chatId) {
      dispatch(
        fetchChatHistory({
          chatId,
          page: 1,
          limit: 50,
        })
      );
    }
  }, [dispatch, chatId]);

  //   const handleSendMessage = () => {
  //     if (newMessage.trim()) {
  //       // TODO: Implement send message functionality
  //       setNewMessage("");
  //     }
  //   };

  //   const handleKeyPress = (e: React.KeyboardEvent) => {
  //     if (e.key === "Enter" && !e.shiftKey) {
  //       e.preventDefault();
  //       handleSendMessage();
  //     }
  //   };

  const handleLoadMore = () => {
    if (hasNextPage && chatId) {
      dispatch(
        fetchChatHistory({
          chatId,
          page: currentPage + 1,
          limit: 50,
        })
      );
    }
  };

  if (isLoading && !chat) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-600">Error loading chat history: {error}</div>
      </div>
    );
  }

  if (!chat) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Chat not found</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate("/messages")}
            className="p-2 hover:bg-gray-100 rounded-xl transition-all duration-200 hover:scale-105"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <div className="flex items-center space-x-4">
            <div className="h-12 w-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-md">
              <span className="text-lg font-bold text-white">
                {chat.participants[0]?.fullName.charAt(0).toUpperCase() || "C"}
              </span>
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {chat.isGroupChat ? "Group Chat" : "Direct Chat"}
              </h2>
              <p className="text-sm text-gray-600 font-medium">
                {chat.participants.map((p: any) => p.fullName).join(", ")}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Load More Button */}
      {hasPrevPage && (
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <button
            onClick={handleLoadMore}
            disabled={isLoading}
            className="w-full py-3 px-4 bg-white text-gray-700 rounded-xl hover:bg-gray-50 disabled:opacity-50 transition-all duration-200 border border-gray-200 shadow-sm hover:shadow-md"
          >
            {isLoading ? (
              <div className="flex items-center justify-center">
                <Loader size="sm" />
                <span className="ml-2 font-medium">Loading...</span>
              </div>
            ) : (
              <span className="font-medium">Load More Messages</span>
            )}
          </button>
        </div>
      )}

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-gradient-to-b from-gray-50 to-white">
        {messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                <MessageCircle className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-gray-500 text-lg font-medium">
                No messages yet
              </p>
              <p className="text-gray-400 text-sm">Start the conversation!</p>
            </div>
          </div>
        ) : (
          messages.map((message: ChatHistoryMessage) => {
            // Check if sender is admin or support user (not a regular participant)
            const isAdminMessage =
              message.sender._id === "admin" ||
              message.sender.email?.includes("admin") ||
              message.sender.email?.includes("support") ||
              message.sender.fullName?.toLowerCase().includes("admin") ||
              message.sender.fullName?.toLowerCase().includes("support");

            return (
              <div
                key={message._id}
                className={`flex ${
                  isAdminMessage ? "justify-end" : "justify-start"
                }`}
              >
                <div
                  className={`max-w-xs lg:max-w-md px-4 py-3 rounded-2xl shadow-sm ${
                    isAdminMessage
                      ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white"
                      : "bg-gray-100 text-gray-900 border border-gray-200"
                  }`}
                >
                  <div className="flex items-center space-x-2 mb-1">
                    <span className="text-xs font-medium">
                      {message.sender.fullName}
                    </span>
                    <span
                      className={`text-xs ${
                        isAdminMessage ? "text-blue-100" : "text-gray-500"
                      }`}
                    >
                      {new Date(message.createdAt).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-sm leading-relaxed">{message.content}</p>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Message Input */}
      {/* <div className="p-4 border-t border-gray-200">
        <div className="flex items-center space-x-3">
          <input
            type="text"
            placeholder="Start typing..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <button
            onClick={handleSendMessage}
            disabled={!newMessage.trim()}
            className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Send className="h-5 w-5" />
          </button>
        </div>
      </div> */}
    </div>
  );
};

export default ChatHistoryPage;
